import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import styled from "styled-components";

// Imported components
import PrimaryButton from "../../../components/ui/PrimaryButton";

// Styled components
const CancelButton = styled(PrimaryButton)`
  background: white;
  border: 1px solid #D9D9D9;
  color: black;
  min-width: 120px;
  width: auto;

  &:hover {
    background: lab(from white calc(l - 5) a b / 1);
  }
`;

const CreateNewButton = styled(PrimaryButton)`
  // align-self: flex-end;
  background: white;
  border: 1px solid #D9D9D9;
  color: black;
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 40px;
  padding: 0 20px;
  width: auto;

  &:hover {
    background: lab(from white calc(l - 5) a b / 1);
  }
`;

const Modal = styled.div`
  align-items: center;
  background-color: #fff;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: ${({ $isOpen }) => ($isOpen ? "flex" : "none")};
  flex-direction: column;
  gap: 8px;
  left: 50%;
  max-height: 80%;
  max-width: 80%;
  min-width: 50%;
  overflow-y: auto;
  padding: 20px;
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  width: auto;
  z-index: 110;
`;

const Overlay = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 109;
`;

const ReturnButton = styled(PrimaryButton)`
  background: var(--terbine-dark-gray);
  color: white;
  min-width: 120px;
  width: auto;

  &:hover {
    background: lab(from var(--terbine-dark-gray) calc(l - 5) a b / 1);
  }
`;

const SaveButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  color: white;
  min-width: 120px;
  width: auto;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 1);
  }
`;

const ValidateButton = styled(PrimaryButton)`
  background: var(--terbine-blue);
  color: white;
  min-width: 120px;
  width: auto;

  &:hover {
    background: lab(from var(--terbine-blue) calc(l - 5) a b / 1);
  }
`;

// Layout component
const GrayBox = ({ children, sx = {} }) => (
  <Box
    sx={{
      backgroundColor: "#f5f5f5",
      p: 2,
      borderRadius: 2,
      mb: 2,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const tempConnectorTypes = ["Kafka"];
const tempCategories = ["Outbound Feeds", "Inbound Feeds"];

// Default form data
const defaultFormData = {
  connectorName: "",
  connectorType: "Kafka",
  category: "Outbound Feeds",
  selectedFeed: "",
  description: "",
};

const NewConnectorModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);

  useEffect(() => {
    const handleUnload = () => {
      setFormData(defaultFormData);
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, []);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // simply toggle state of the modal
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  return (
    <div>
      <CreateNewButton onClick={toggleModal}>+ Create New</CreateNewButton>

      <Overlay $isOpen={isModalOpen} onClick={toggleModal} />

      <Modal $isOpen={isModalOpen} id="modal">
        <Typography variant="h4" sx={{ color: "black", mb: 1 }} gutterBottom>
          Configure New Connector
        </Typography>

        <Grid container direction="row" spacing={2} wrap="nowrap">
          <Grid item xs={3} sm={6}>
            <GrayBox>
              <Typography variant="body1" sx={{ color: "black", mb: 1 }}>
                Connector Name *
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.connectorName}
                onChange={(e) => updateField("connectorName", e.target.value)}
                InputProps={{ sx: { backgroundColor: "white" } }}
              />
            </GrayBox>
          </Grid>

          <Grid item xs={3} sm={6}>
            <GrayBox>
              <Typography variant="body1" sx={{ color: "black", mb: 1 }}>
                Connector Type *
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                value={formData.connectorType}
                onChange={(e) => updateField("connectorType", e.target.value)}
                InputProps={{ sx: { backgroundColor: "white" } }}
              >
                {tempConnectorTypes.map((connectorType) => (
                  <MenuItem key={connectorType} value={connectorType}>
                    {connectorType}
                  </MenuItem>
                ))}
              </TextField>
            </GrayBox>
          </Grid>
        </Grid>

        <Grid container direction="row" spacing={2} wrap="nowrap">
          <Grid item xs={3} sm={6}>
            <GrayBox>
              <Typography variant="body1" sx={{ color: "black", mb: 1 }}>
                Category
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                value={formData.category}
                onChange={(e) => updateField("category", e.target.value)}
                InputProps={{ sx: { backgroundColor: "white" } }}
              >
                {tempCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </GrayBox>
          </Grid>

          <Grid item xs={3} sm={6}>
            <GrayBox>
              <Typography variant="body1" sx={{ color: "black", mb: 1 }}>
                Selected Feed
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formData.selectedFeed}
                onChange={(e) => updateField("selectedFeed", e.target.value)}
                InputProps={{ sx: { backgroundColor: "white" } }}
              />
            </GrayBox>
          </Grid>
        </Grid>

        <Box sx={{ width: "100%" }}>
          <GrayBox>
            <Typography variant="body1" sx={{ color: "black", mb: 1 }}>
              Description
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={formData.description}
              onChange={(e) => updateField("description", e.target.value)}
              InputProps={{ sx: { backgroundColor: "white" } }}
            />
          </GrayBox>
        </Box>

        <Box sx={{ width: "50%" }}>
          <GrayBox
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="h5" sx={{ color: "black", mb: 1 }}>
              No Configured Parameters
            </Typography>
            <ValidateButton type="button">Validate Configuration</ValidateButton>
          </GrayBox>
        </Box>

        {/* <Grid container direction="row" spacing={2} wrap="nowrap">
          <Grid item xs={3} sm={4}>
            <ReturnButton>Return</ReturnButton>
          </Grid>
          <Grid item xs={3} sm={4}>
            <CancelButton>Cancel</CancelButton>
          </Grid>
          <Grid item xs={3} sm={4}>
            <SaveButton>Save</SaveButton>
          </Grid>
        </Grid> */}

        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-around", width: "50%" }}>
          <ReturnButton onClick={toggleModal} type="button">Return</ReturnButton>
          <CancelButton onClick={toggleModal} type="button">Cancel</CancelButton>
          <SaveButton onClick={() => {}} type="submit">Save</SaveButton>
        </Box>
      </Modal>
    </div>
  );
};

export default NewConnectorModal;
